// Lightweight telemetry mirroring the original add-in:
//  - Error reporting → POST /api/error-reports (anonymous; active once CORS is enabled).
//  - Usage sessions → POST /api/usage/sessions (per-mode snip counts; requires a signed-in
//    user; flushed when the task pane is hidden/closed).
import * as api from './api'
import { config } from './config'
import { MODE_LABELS } from './modes'
import type { SnipMode } from '../types'

const counts: Record<string, number> = {}
let sessionId = ''
let sessionStart = 0

function ensureSession(): string {
  if (!sessionId) {
    sessionId = crypto.randomUUID()
    sessionStart = Date.now()
  }
  return sessionId
}

export function recordSnip(mode: SnipMode): void {
  ensureSession()
  // Key by PascalCase mode name ("Text", "Sum", …) to match the original add-in's
  // /api/usage/sessions payload so existing dashboards keep working.
  const key = MODE_LABELS[mode]
  counts[key] = (counts[key] ?? 0) + 1
}

export async function flushUsage(): Promise<void> {
  if (!api.getAccessToken()) return // usage telemetry requires an authenticated session
  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  if (total === 0) return
  const payload = {
    sessionId: ensureSession(),
    startedAt: new Date(sessionStart).toISOString(),
    endedAt: new Date().toISOString(),
    clientInfo: api.clientInfo(),
    snipCounts: { ...counts },
  }
  for (const k of Object.keys(counts)) delete counts[k] // reset before await to avoid double-send
  await api.postUsage(payload)
}

export function installTelemetry(): void {
  window.addEventListener('error', (e) => {
    void api.reportError(buildReport(e.error ?? e.message, 'window.onerror'))
  })
  window.addEventListener('unhandledrejection', (e) => {
    void api.reportError(buildReport((e as PromiseRejectionEvent).reason, 'unhandledrejection'))
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushUsage().catch(() => {})
  })
}

function buildReport(err: unknown, context: string): Record<string, unknown> {
  const e = err instanceof Error ? err : new Error(String(err))
  return {
    userMessage: '',
    context,
    exceptionType: e.name,
    exceptionMessage: e.message,
    stackTrace: e.stack ?? '',
    appVersion: config.appVersion,
    platform: 'Web',
    timestampUtc: new Date().toISOString(),
  }
}
